<template>
  <tbody class="table-empty-state-tbody">
    <tr>
      <td :colspan="colspan" class="table-empty-state-cell">
        <div class="empty-state-wrapper">
          <!-- Icon Container -->
          <div class="empty-state-icon-box" :class="{ 'is-filtered': isFiltered }">
            <slot name="icon">
              <i :class="currentIcon" class="empty-state-main-icon"></i>
              <span v-if="isFiltered" class="empty-state-sub-badge" title="Filtered results">
                <i class="fa-solid fa-filter"></i>
              </span>
            </slot>
          </div>

          <!-- Title -->
          <h5 class="empty-state-title">
            <slot name="title">
              {{ isFiltered ? (filteredTitle || 'No matching records found') : (title || 'No records to display') }}
            </slot>
          </h5>

          <!-- Description -->
          <p class="empty-state-description">
            <slot name="description">
              {{ isFiltered ? (filteredDescription || "We couldn't find any results matching your search or active filters. Try adjusting your search query or clearing filters.") : description }}
            </slot>
          </p>

          <!-- Actions -->
          <div class="empty-state-actions">
            <slot name="actions">
              <button
                v-if="isFiltered"
                type="button"
                class="btn empty-state-btn-secondary"
                @click="$emit('clear-filters')"
              >
                <i class="fa-solid fa-rotate-left me-1"></i>
                <span>{{ clearFiltersText || 'Reset Filters & Search' }}</span>
              </button>

              <button
                v-else-if="showAction && actionText"
                type="button"
                :class="actionClass || 'btn empty-state-btn-primary'"
                :data-bs-toggle="actionModalTarget ? 'modal' : null"
                :data-bs-target="actionModalTarget ? actionModalTarget : null"
                @click="$emit('action')"
              >
                <i v-if="actionIcon" :class="actionIcon" class="me-1"></i>
                <span>{{ actionText }}</span>
              </button>
            </slot>
          </div>

          <!-- Extra content slot (e.g., tips, badges) -->
          <div v-if="$slots.extra" class="empty-state-extra mt-3">
            <slot name="extra"></slot>
          </div>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script>
export default {
  name: 'TableEmptyState',
  props: {
    colspan: {
      type: [Number, String],
      required: true,
      default: 1,
    },
    icon: {
      type: String,
      default: 'fa-solid fa-folder-open',
    },
    title: {
      type: String,
      default: 'No records to display',
    },
    description: {
      type: String,
      default: '',
    },
    isFiltered: {
      type: Boolean,
      default: false,
    },
    filteredIcon: {
      type: String,
      default: 'fa-solid fa-magnifying-glass',
    },
    filteredTitle: {
      type: String,
      default: 'No matching records found',
    },
    filteredDescription: {
      type: String,
      default: '',
    },
    actionText: {
      type: String,
      default: '',
    },
    actionIcon: {
      type: String,
      default: 'fa-solid fa-plus',
    },
    actionClass: {
      type: String,
      default: 'btn empty-state-btn-primary',
    },
    actionModalTarget: {
      type: String,
      default: '',
    },
    showAction: {
      type: Boolean,
      default: true,
    },
    clearFiltersText: {
      type: String,
      default: 'Reset Filters & Search',
    },
  },
  emits: ['action', 'clear-filters'],
  computed: {
    currentIcon() {
      if (this.isFiltered) {
        return this.filteredIcon || 'fa-solid fa-magnifying-glass';
      }
      return this.icon || 'fa-solid fa-folder-open';
    },
  },
};
</script>

<style scoped>
.table-empty-state-tbody {
  border-top: none !important;
}

.table-empty-state-cell {
  background-color: #ffffff;
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 0 !important;
}

.empty-state-wrapper {
  padding: 56px 24px;
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  animation: emptyStateFadeIn 0.28s ease-out;
}

@keyframes emptyStateFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.empty-state-icon-box {
  position: relative;
  width: 74px;
  height: 74px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(0, 160, 193, 0.12) 0%, rgba(0, 160, 193, 0.04) 100%);
  border: 1px solid rgba(0, 160, 193, 0.2);
  box-shadow: 0 4px 16px rgba(0, 160, 193, 0.08), 0 0 0 6px rgba(0, 160, 193, 0.03);
}

.empty-state-icon-box.is-filtered {
  background: linear-gradient(135deg, rgba(100, 116, 139, 0.12) 0%, rgba(100, 116, 139, 0.04) 100%);
  border-color: rgba(100, 116, 139, 0.22);
  box-shadow: 0 4px 16px rgba(100, 116, 139, 0.08), 0 0 0 6px rgba(100, 116, 139, 0.03);
}

.empty-state-main-icon {
  font-size: 28px;
  color: #00A0C1;
  transition: transform 0.2s ease;
}

.empty-state-icon-box.is-filtered .empty-state-main-icon {
  color: #64748b;
}

.empty-state-sub-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00A0C1;
  color: #ffffff;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.empty-state-title {
  font-size: 16.5px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
  letter-spacing: -0.2px;
}

.empty-state-description {
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.55;
  margin-bottom: 22px;
  font-weight: 400;
}

.empty-state-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}

.empty-state-btn-primary {
  background-color: #00A0C1 !important;
  border: 1px solid #00A0C1 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 13.5px !important;
  height: 38px;
  padding: 0 1.25rem;
  border-radius: 8px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
  transition: all 0.2s ease;
  cursor: pointer;
}

.empty-state-btn-primary:hover {
  background-color: #008ba8 !important;
  border-color: #008ba8 !important;
  color: #ffffff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 160, 193, 0.35);
}

.empty-state-btn-secondary {
  background-color: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  color: #475569 !important;
  font-weight: 600 !important;
  font-size: 13.5px !important;
  height: 38px;
  padding: 0 1.25rem;
  border-radius: 8px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
}

.empty-state-btn-secondary:hover {
  background-color: #f8fafc !important;
  border-color: #94a3b8 !important;
  color: #0f172a !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
</style>
